import * as React from "react";

import Form, { FormComponentProps } from "@ant-design/compatible/es/form";
import { LockOutlined } from "@ant-design/icons";
import HCaptcha from "@hcaptcha/react-hcaptcha";
import FormCard from "@impero-com/common/components/formCard";
import FormCardContainer from "@impero-com/common/components/formCardContainer";
import RightSideIcon from "@impero-com/common/components/rightSideIcon";
import passwordSvg from "@impero-com/common/img/password-login.svg";
import { SwitchToTwoFactor } from "@impero-com/common/types/switchToTwoFactor";
import { AjaxManager } from "@impero-com/common/utils/ajax";
import {
    isLoginTimeout,
    isUnauthenticated,
} from "@impero-com/common/utils/auth";
import { t } from "@impero-com/common/utils/i18n";
import { OrgCookie } from "@impero-com/common/utils/orgCookie";
import { Button, CardFooter, FormSubtitle, FormTitle } from "@impero-com/ui";
import { InputRef, Input } from "antd";
import axios from "axios";

import { postValidatePin } from "api/generated/two-factor/two-factor";

import { ENVIRONMENT } from "../../env";

import * as theme from "./theme.css";

const FormItem = Form.Item;

class RecoveryFormInternal extends React.PureComponent<
    RecoveryFormProps,
    RecoveryFormState
> {
    private pinInput: React.RefObject<InputRef | null>;
    private captchaRef: React.RefObject<HCaptcha | null>;

    constructor(props: RecoveryFormProps) {
        super(props);
        this.pinInput = React.createRef();
        this.captchaRef = React.createRef();

        this.state = {
            status: { type: "DEFAULT" },
            requiresHumanVerification: false,
        };
    }

    componentDidMount() {
        if (this.pinInput.current) {
            this.pinInput.current.focus();
        }
    }

    handleSubmit = (e: React.FormEvent) => {
        const { challenge, onSwitchToTwoFactor } = this.props;
        e.preventDefault();
        this.props.form.validateFields(
            (err: any, values: RecoveryFormValues) => {
                if (!err) {
                    const { pin, captcha } = values;
                    if (this.state.status.type !== "DEFAULT") {
                        return;
                    }
                    this.setState({ status: { type: "LOGGING_IN" } });
                    // TODO: migrate to the AJAX manager
                    postValidatePin({
                        pin: pin ?? "",
                        challenge,
                        human_verification_response: captcha ?? null,
                    })
                        .then((res) => {
                            const { recoveryCode } = res.data;
                            if (
                                recoveryCode !== null &&
                                recoveryCode !== undefined
                            ) {
                                onSwitchToTwoFactor({
                                    type: "DISPLAY_RECOVERY",
                                    recoveryCode,
                                });
                            }
                            this.setState({ status: { type: "DEFAULT" } });
                        })
                        .catch((err: unknown) => {
                            const response = axios.isAxiosError(err)
                                ? err.response
                                : undefined;
                            if (
                                response?.status === 408 &&
                                isLoginTimeout(response.data) &&
                                response.data.cause === "credentials"
                            ) {
                                this.props.onNeedCredentials();
                            }
                            this.setState((state) => ({
                                status: { type: "DEFAULT" },
                                requiresHumanVerification:
                                    state.requiresHumanVerification ||
                                    (response?.status === 401 &&
                                        isUnauthenticated(response.data) &&
                                        response.data.status ===
                                            "notAuthenticated" &&
                                        response.data
                                            .requiresHumanVerification),
                            }));
                        });
                    this.resetCaptcha();
                }
            }
        );
    };

    handleCaptchaVerify = (captcha: string) => {
        this.props.form.setFieldsValue({ captcha });
    };

    // Captchas are one-time-usage
    resetCaptcha = () => {
        if (this.captchaRef.current) {
            this.captchaRef.current.resetCaptcha();
            this.props.form.setFieldsValue({ captcha: undefined });
        }
    };

    render() {
        const { orgCookie } = this.props;
        const { requiresHumanVerification } = this.state;
        const { getFieldDecorator } = this.props.form;

        return (
            <FormCardContainer
                rightSide={
                    <RightSideIcon
                        icon={passwordSvg}
                        customImages={true}
                        orgCookie={orgCookie}
                    />
                }
            >
                <FormTitle title={t("Setup2FA_RecoveryCode")} />
                <FormSubtitle
                    subtitle={t("Login2FA_RecoveryCodeDescription")}
                />
                <FormCard onSubmitForm={this.handleSubmit}>
                    <FormItem>
                        {getFieldDecorator("pin", {
                            rules: [
                                {
                                    required: true,
                                    message: t("Login_PinCodeOrRecoveryCode"),
                                },
                            ],
                        })(
                            <Input
                                prefix={
                                    <LockOutlined
                                        className={theme.iconOpacity}
                                    />
                                }
                                placeholder={t("Setup2FA_RecoveryCode")}
                                ref={this.pinInput}
                            />
                        )}
                    </FormItem>
                    {requiresHumanVerification ? (
                        <FormItem className={theme.hcaptchaContainer}>
                            {getFieldDecorator("captcha", {
                                rules: [{ required: true }],
                            })(
                                <HCaptcha
                                    size="normal"
                                    sitekey={
                                        ENVIRONMENT.IMPERO_HCAPTCHA_SITE_KEY
                                    }
                                    onVerify={this.handleCaptchaVerify}
                                    ref={this.captchaRef}
                                />
                            )}
                        </FormItem>
                    ) : null}
                    <Button
                        id="recovery-code"
                        variant="primary"
                        type="submit"
                        block
                    >
                        {t("Login_LoginButton")}
                    </Button>
                </FormCard>

                <CardFooter>
                    <Button
                        id="recovery-link"
                        variant="link"
                        onClick={this.handleSwitchToTwoFactor}
                    >
                        {t("Login2FA_LoginWithPin")}
                    </Button>
                </CardFooter>
            </FormCardContainer>
        );
    }

    handleSwitchToTwoFactor = () => {
        const { challenge, onSwitchToTwoFactor } = this.props;
        onSwitchToTwoFactor({ type: "DEFAULT", challenge });
    };
}

interface RecoveryFormValues {
    pin?: string;
    captcha?: string;
}

interface RecoveryFormProps extends FormComponentProps {
    onSwitchToTwoFactor: (params: SwitchToTwoFactor) => void;
    onNeedCredentials: () => void;
    challenge: string;
    ajaxManager: AjaxManager;
    orgCookie: OrgCookie;
}

type RecoveryFormStatus = { type: "DEFAULT" } | { type: "LOGGING_IN" };
interface RecoveryFormState {
    status: RecoveryFormStatus;
    requiresHumanVerification: boolean;
}

const RecoveryForm = Form.create<RecoveryFormProps>()(RecoveryFormInternal);

export default RecoveryForm;
